// =============================================================================
// Origin UI — src/components/HelpChatWidget.tsx
// Floating help assistant chat widget. Renders on every page.
// Persists conversation in React state across navigation.
// =============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { helpApi } from '@/services/helpApi';
import type { ChatMessageDto } from '@/services/helpApi';
import styles from './HelpChatWidget.module.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  feedbackSent?: boolean;
}

const WELCOME_MESSAGE: Message = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hi! I'm the Origin Help Assistant. I can help you with anything in Origin \u2014 booking leave, submitting requests, generating letters, finding your way around. What can I help you with?",
};

// Shared state so the widget survives navigation between AppLayout and HomePage
let sharedMessages: Message[] = [WELCOME_MESSAGE];
let sharedIsOpen = false;

export function HelpChatWidget() {
  const [isOpen, setIsOpen] = useState(sharedIsOpen);
  const [messages, setMessages] = useState<Message[]>(sharedMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync shared state
  useEffect(() => {
    sharedMessages = messages;
  }, [messages]);

  useEffect(() => {
    sharedIsOpen = isOpen;
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      // Build conversation history (exclude welcome message)
      const history: ChatMessageDto[] = [...messages, userMsg]
        .filter((m) => m.id !== 'welcome')
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await helpApi.sendMessage(trimmed, history);

      const assistantMsg: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.response,
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch {
      const errorMsg: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "Sorry, I couldn't process your request right now. Please try again in a moment.",
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFeedback = async (msg: Message) => {
    // Find the user message that preceded this assistant message
    const idx = messages.findIndex((m) => m.id === msg.id);
    const userMsg = messages
      .slice(0, idx)
      .reverse()
      .find((m) => m.role === 'user');

    if (!userMsg) return;

    try {
      await helpApi.submitFeedback(userMsg.content, msg.content);
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, feedbackSent: true } : m))
      );
    } catch {
      // Silently fail — not critical
    }
  };

  const hasConversation = messages.length > 1;

  return (
    <>
      {/* Floating button */}
      <button
        className={styles.floatingButton}
        onClick={() => setIsOpen(!isOpen)}
        title="Origin Help"
        aria-label="Open help assistant"
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        )}
        {hasConversation && !isOpen && <span className={styles.activeDot} />}
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div className={styles.panel}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              <svg width="20" height="20" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8 }}>
                <path d="M4 44 C8 32 16 20 26 14 C34 9 42 10 48 18" fill="none" stroke="#00B0B9" strokeWidth="5.5" strokeLinecap="round"/>
                <path d="M8 6 C12 18 20 30 30 36 C38 41 46 40 50 32" fill="none" stroke="#FF8300" strokeWidth="5.5" strokeLinecap="round"/>
                <circle cx="26" cy="25" r="4.5" fill="#FFFFFF"/>
              </svg>
              Origin Help
            </div>
            <button className={styles.closeButton} onClick={() => setIsOpen(false)} aria-label="Close help">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className={styles.messages}>
            {messages.map((msg) => (
              <div key={msg.id} className={`${styles.messageRow} ${msg.role === 'user' ? styles.userRow : styles.assistantRow}`}>
                <div className={`${styles.bubble} ${msg.role === 'user' ? styles.userBubble : styles.assistantBubble}`}>
                  {msg.role === 'assistant' ? (
                    <div className={styles.markdownContent} dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                  ) : (
                    msg.content
                  )}
                </div>
                {msg.role === 'assistant' && msg.id !== 'welcome' && (
                  <div className={styles.feedbackRow}>
                    {msg.feedbackSent ? (
                      <span className={styles.feedbackThanks}>Thanks for the feedback</span>
                    ) : (
                      <button
                        className={styles.feedbackButton}
                        onClick={() => handleFeedback(msg)}
                        title="This wasn't helpful"
                        aria-label="Flag this response"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 14V2" />
                          <path d="M9 18.12L10 14H4.17a2 2 0 01-1.92-2.56l2.33-8A2 2 0 016.5 2H20a2 2 0 012 2v8a2 2 0 01-2 2h-2.76a2 2 0 00-1.79 1.11L12 22a3 3 0 01-3-3.88z" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className={`${styles.messageRow} ${styles.assistantRow}`}>
                <div className={`${styles.bubble} ${styles.assistantBubble} ${styles.typingBubble}`}>
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={styles.inputBar}>
            <input
              ref={inputRef}
              className={styles.input}
              type="text"
              placeholder="Ask me anything about Origin..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
            />
            <button
              className={styles.sendButton}
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              aria-label="Send message"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Very simple markdown renderer for assistant messages.
 * Handles: bold, bullet lists, numbered lists, inline code, links.
 */
function renderMarkdown(text: string): string {
  let html = text
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Inline code
    .replace(/`(.+?)`/g, '<code>$1</code>')
    // Links
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  // Process lines for lists
  const lines = html.split('\n');
  const result: string[] = [];
  let inUl = false;
  let inOl = false;

  for (const line of lines) {
    const ulMatch = line.match(/^[-*]\s+(.+)/);
    const olMatch = line.match(/^\d+\.\s+(.+)/);

    if (ulMatch) {
      if (!inUl) { result.push('<ul>'); inUl = true; }
      if (inOl) { result.push('</ol>'); inOl = false; }
      result.push(`<li>${ulMatch[1]}</li>`);
    } else if (olMatch) {
      if (!inOl) { result.push('<ol>'); inOl = true; }
      if (inUl) { result.push('</ul>'); inUl = false; }
      result.push(`<li>${olMatch[1]}</li>`);
    } else {
      if (inUl) { result.push('</ul>'); inUl = false; }
      if (inOl) { result.push('</ol>'); inOl = false; }
      if (line.trim()) {
        result.push(`<p>${line}</p>`);
      }
    }
  }

  if (inUl) result.push('</ul>');
  if (inOl) result.push('</ol>');

  return result.join('');
}
